import { RevenueItem } from '../../../revenue/shared/revenue-items/revenue-item.model';
import { RevenueItemService } from '../../../revenue/shared/revenue-items/revenue-item.service';
import { Component, OnInit, OnChanges, Input, ViewChild, OnDestroy } from '@angular/core';
import { TranslationService } from '../../../shared/translation/translation.service';
import { ProfileService } from '../../shared/profile.service';
import { ChartService } from '../../../shared/chart/chart.service';
import { ChartData } from '../../../shared/chart/chart-data.model';
import { UIChart } from 'primeng/chart';
import { AgingSummaryRow } from '../../../revenue/shared/aging-summary.model';
import { Subscription } from 'rxjs';
import { ObservationService } from 'src/app/shared/observation/observation.service';
import { BillingDto } from 'src/app/revenue/shared/billing/dto/billing.dto';

@Component({
  selector: 'wl-profile-view-revenue',
  templateUrl: 'revenue.component.html',
  styleUrls: ['revenue.component.css'],
  providers: [ChartService],
    standalone: false
})
export class ProfileViewRevenueComponent implements OnInit, OnChanges, OnDestroy {
  private _profileSubscription: Subscription;

  public colors: Array<string>;
  public totalInvoiced: number;
  public chartData: ChartData;
  public annualDues: number;
  public duesColor: string;
  public nonDuesColor: string;
  public isLoading = true;
  public billings: Array<BillingDto>;

  public chartOptions: any;

  @Input() public profileId: number;
  @ViewChild('invoiceChart', { static: false }) invoiceChart: UIChart;

  public agingData: Array<AgingSummaryRow>;

  public constructor(
    private _profileService: ProfileService,
    private _translationService: TranslationService,
    private _chartService: ChartService,
    private _observationService: ObservationService,
    private _revenueItemService: RevenueItemService
  ) {
    this.isLoading = true;
    this.chartData = new ChartData();
    this.chartOptions = {
      responsive: false,
      plugins: {
        legend: {
          position: false,
        },
      },
    };

    this.duesColor = '';
    this.nonDuesColor = '';
  }

  ngOnInit() {
    this._profileSubscription = this._observationService.observeProfiles().subscribe((id) => {
      this.profileUpdated(id);
    });
  }

  ngOnChanges() {
    this.loadData(true);
  }

  ngOnDestroy() {
    if (this._profileSubscription) {
      this._profileSubscription.unsubscribe();
    }
  }

  private profileUpdated(id: number) {
    if (id === this.profileId) {
      this.loadData(true);
    }
  }
  private loadData(forceRefresh?: boolean) {
    this.chartData = new ChartData();
    this.chartOptions = {
      responsive: false,
      plugins: {
        legend: {
          position: false,
        },
      },
    };

    this.duesColor = '';
    this.nonDuesColor = '';

    this.totalInvoiced = 0;
    this.annualDues = 0;

    this._profileService.getProfileInvoiceHistory(this.profileId, forceRefresh).then((data: Array<any>) => {
      for (const row of data) {
        this.chartData.labels.push(row.Item1);
        this.chartData.datasets[0].data.push(row.Item2);
      }

      this.colors = this._chartService.getChartColors(data.length);
      this.chartData.datasets[0].backgroundColor = this.colors;
      this.duesColor = this.colors[0];
      this.nonDuesColor = this.colors[1];

      this.invoiceChart.refresh();

      const add = (a: number, b: number) => a + b;
      this.totalInvoiced = this.chartData.datasets[0].data.reduce(add);
    });

    this._profileService.getProfileAgingSummary(this.profileId, forceRefresh).then((data: Array<AgingSummaryRow>) => {
      this.agingData = this.formatAgingData(data);
      this.agingData.splice(5, 1)[0];
    });

    this._profileService.getProfileAnnualDues(this.profileId, forceRefresh).then((data: number) => {
      this.annualDues = data;
    });

    this._profileService.getProfileBillings(this.profileId, forceRefresh).then((data: Array<BillingDto>) => {
      this.billings = data;

      for (const billing of this.billings) {
        if (billing.ItemNum) {
          this._revenueItemService.getRevenueItem(billing.ItemNum).then((item: RevenueItem) => {
            billing.RevenueItem = item.ItemName;
          });
        }
      }
    });

    this.isLoading = false;
  }

  private formatAgingData(data: Array<AgingSummaryRow>): Array<AgingSummaryRow> {
    for (let i = 0; i < data.length; i++) {
      switch (i) {
        case 0: {
          data[i].Title = this._translationService.getInstant('profiles.view.revenue.aging.current');
          break;
        }
        case 1: {
          data[i].Title = this._translationService.getInstant('profiles.view.revenue.aging.30');
          break;
        }
        case 2: {
          data[i].Title = this._translationService.getInstant('profiles.view.revenue.aging.60');
          break;
        }
        case 3: {
          data[i].Title = this._translationService.getInstant('profiles.view.revenue.aging.90');
          break;
        }
        case 4: {
          data[i].Title = this._translationService.getInstant('profiles.view.revenue.aging.120');
          break;
        }
      }
    }

    return data;
  }
}
